/* eslint-disable react/prop-types */
import React, { CSSProperties } from 'react';
import Style from './index.less';
import Arrow from '../../images/unfold.svg';
import cns from 'classnames';
import { NUMBER_SLIDE_SIZE } from '../constants';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

interface NumberInputProps {
    value: string|number;
    valuePostFix: string;
    step: number;
    toFixed: number;
    min: number;
    max: number;
    allowDecimal: boolean;
    disabled: boolean;
    onBlur?: (e?) => void;
    onFocus?: (e?) => void;
    onValueChange: (value) => void;
    onMouseEnter?: (e?) => void;
    size?: string;
    maxLength?: number;
    style: CSSProperties;
    inputStyle?: CSSProperties;
    buttonStyle?: CSSProperties;
    buttonWrapperStyle?: CSSProperties;
}

/**
 * 输入框，包含增加，减少按钮
 */
export default class NumberInput extends React.Component<NumberInputProps> {
    public static contextTypes = {
    };

    public static defaultProps = {
        valuePostFix: '',
        step: 1,
        toFixed: 0,
        min: 0,
        max: 100,
        allowDecimal: false,
        disabled: false,
    };

    public hasFocus: boolean;
    private oldValue: string|number|null;
    private inputRef: HTMLInputElement|null;
    private startValue: any;
    public constructor(props, context) {
        super(props, context);

        this.oldValue = null;
        this.hasFocus = false;
    }

    public componentDidMount() {
        this.oldValue = this.props.value;
    }

    public componentWillUnmount() {
        this.getEditingStateManager().isEditing = false;
        this.props.onBlur?.();
    }

    public render() {
        const {
            maxLength, value, style, inputStyle, disabled, size, buttonStyle, buttonWrapperStyle,
        } = this.props;
        const maxLen = typeof (maxLength) === 'undefined' ? 50 : maxLength;
        const isSmall = size === NUMBER_SLIDE_SIZE.SMALL;
        const inputClass = cns(Style['number-input'], { [Style['number-input-small']]: isSmall });
        const inputBtnClass = cns(Style['number-input-buttons'], { [Style['number-input-buttons-small']]: isSmall });
        const upBtnClass = cns(Style['up-button'], { [Style['up-button-small']]: isSmall });
        const downBtnClass = cns(Style['down-button'], { [Style['down-button-small']]: isSmall });
        const arrowIconClass = cns(Style['arrow-icon'], { [Style['arrow-icon-small']]: isSmall });
        return (
            <div className={cns(Style['number-input-wrapper'], {
                [Style['disabled-number-input-wrapper']]: disabled,
                [Style['number-input-wrapper-small']]: isSmall,
            })} style={style}>
                <input disabled={disabled} className={inputClass}
                    maxLength={maxLen} value={value}
                    onChange={this.handleValueChange}
                    ref={(ref) => {
                        this.inputRef = ref;
                    }}
                    onMouseEnter={this.props.onMouseEnter}
                    onBlur={this.handleBlur}
                    onFocus={this.handleFocus}
                    style={inputStyle} />
                <div className={inputBtnClass} style={buttonWrapperStyle}>
                    <div className={upBtnClass} onMouseDown={this.handleAddClick} style={buttonStyle}>
                        <img src={Arrow} className={arrowIconClass} style={{ transform: 'rotate(-90deg)' }} />
                    </div>
                    <div className={downBtnClass} onMouseDown={this.handleReduceClick} style={buttonStyle}>
                        <img src={Arrow} className={arrowIconClass} style={{ transform: 'rotate(90deg)' }} />
                    </div>
                </div>
            </div>
        );
    }

    private valuePostFix = () => this.props.valuePostFix;

    private handleValueChange = (e) => {
        const { value } = e.target;
        // const hasPoint = !Number.isInteger(step);
        const hasPoint = this.props.allowDecimal;

        // 为单位正则增加括号，例如 厘米？-> (厘米)？，避免单位是多个字符时匹配错误，下同
        const reg = new RegExp(`^-?\\d*${hasPoint ? '\\.?' : ''}\\d{0,3}(${this.valuePostFix()})?$`);
        const stopInput = this.getStopInputFromValue(value);
        if (!stopInput && value.match(reg)) {
            if (value) {
                const number = parseFloat(value);
                if (isNaN(number)) {
                    this.onValueChange(value);
                    return;
                }
                const { min, max } = this.props;
                if (number >= min && number <= max) {
                    this.oldValue = value;
                    this.onValueChange(value);
                } else {
                    this.onValueChange(this.oldValue);
                }
            } else {
                this.oldValue = value;
                this.onValueChange(value);
            }
        } else {
            // this.onValueChange(this.oldValue); //这里会把当前值替换掉
        }
    };

    private handleAddClick = (e) => {
        if (this.props.disabled) return;

        e.preventDefault();
        const { max } = this.props;
        let number = parseFloat(this.inputRef?.value || '');
        if (isNaN(number)) {
            number = 0;
        }
        const { step } = this.props;
        if (number + step <= max) {
            let rv = number + step;

            // 对于0.5 + 0.1 = 0.5999999的浮点数相加问题，floor会导致步长为小数的操作失败
            if (step < 1) {
                rv = Math.round(rv / step) * step;
            } else {
                rv = Math.floor(rv / step) * step; // 值始终为step的倍数
            }
            const value = `${rv}${this.valuePostFix()}`;
            this.oldValue = value;
            this.onValueChange(value);
        } else {
            const { min } = this.props;
            const value = `${min}${this.valuePostFix()}`;
            this.oldValue = value;
            this.onValueChange(value);
        }
    };

    private handleReduceClick = (e) => {
        if (this.props.disabled) return;

        e.preventDefault();
        const { min } = this.props;
        let number = parseFloat(this.inputRef?.value || '');
        if (isNaN(number)) {
            number = 0;
        }
        const step = this.props.step ? this.props.step : 1;
        if (number - step >= min) {
            let rv = number - step;

            // 解决浮点数相减问题
            if (step < 1) {
                rv = Math.round(rv / step) * step;
            } else {
                rv = Math.ceil(rv / step) * step; // 值始终为step的倍数
            }
            const value = `${rv}${this.valuePostFix()}`;
            this.oldValue = value;
            this.onValueChange(value);
        } else {
            const { min } = this.props;
            const value = `${min}${this.valuePostFix()}`;
            this.oldValue = value;
            this.onValueChange(value);
        }
    };

    private handleBlur = (e) => {
        this.hasFocus = false;
        this.getEditingStateManager().isEditing = false;

        let { value } = e.target;
        if (this.startValue !== value) {
            // 解决多个字符的单位匹配问题
            const reg = new RegExp(`^\\d*(${this.valuePostFix()})?$`);
            if (isNaN(parseFloat(value))) {
                value = 0;
            } else if (value?.match(reg)) {
                // value有正常的值，不使用oldValue
            } else {
                value = this.oldValue ? this.oldValue : 0;
            }
            this.onValueChange(`${parseFloat(value)}${this.valuePostFix()}`);
        }

        this.props.onBlur?.(e);
    };

    private handleFocus = (e) => {
        this.hasFocus = true;
        this.getEditingStateManager().isEditing = true;
        this.startValue = e.target.value;
        this.oldValue = e.target.value;
        this.props.onFocus?.(e);
    };

    private onValueChange = (value) => {
        this.props.onValueChange?.(value);
    };

    private getStopInputFromValue(value) {
        let stopInput = false;
        if (this.props.toFixed) {
            const n = parseFloat(value);
            const array = n.toString().split('.');
            const len = n.toString().length;
            if (array.length > 1) {
                stopInput = this.isDecimalsOk(array, value, len);
            }
            if (array.length === 1 && !isNaN(n) && value[len] === '.') {
                // 如果是整数，检查是否存在.00的情况
                stopInput = this.isIntegerOk(value, len);
            }
        }
        return stopInput;
    }

    private isIntegerOk(value, len) {
        let zeroSum = 0;
        while (value[len + zeroSum + 1] === '0') {
            zeroSum += 1;
        }
        if (zeroSum > this.props.toFixed) {
            return true;
        }
        return false;
    }

    private isDecimalsOk(array, value, len) {
        const sn = array[1].length;
        if (sn > this.props.toFixed) {
            return true;
        }
        if (sn <= this.props.toFixed) { // 如果小数位满足条件，检查是否是.200的情况
            let zeroSum = 0;
            const restFixed = this.props.toFixed - sn;
            while (value[len + zeroSum] === '0') {
                zeroSum += 1;
            }
            if (zeroSum > restFixed) {
                return true;
            }
        }
        return false;
    }

    private getEditingStateManager = () => (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
        .viewableServiceLayer.editingStateManager;
}
